import { useState, useEffect } from "react";
import { Plus, Brain, MessageSquare, BarChart3, Globe } from "lucide-react";
import DashboardNavbar from "../../reusableComponents/Navbars/DashboardNavbar";
import DashboardLayout from "../../reusableComponents/LayoutContainers/DashboardLayout";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "../../components/SoftButton";
import CategoryCard from "./components/CategoryCard";
import EditCategoryModal from "./components/EditCategoryModal";
import QuestionManager from "./components/QuestionManager";
import {
    addDigitizationCategory,
    deleteDigitizationCategory,
    getDigitizationCategories,
    updateDigitizationCategory
} from "../../axios-client";
import SnackbarNotification from "../../reusableComponents/Snackbar/SnackbarNotification";
import i18n from "../../config/i18n";

const ManageAiAssistant = () => {
    const language = i18n.language;
    const [categories, setCategories] = useState([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [notification, setNotification] = useState({
        show: false,
        snackbarMessage: '',
        severity: '',
    });

    const fetchDigitizationCategories = async () => {
        try {
            const res = await getDigitizationCategories();
            setCategories(res.data)
        } catch (error) {
            console.error("Failed to update category:", error);
            setNotification({
                show: true,
                snackbarMessage: 'An error occurred. Please try again.',
                severity: 'error',
            });
        }
    }
    useEffect(() => {
        fetchDigitizationCategories()
    }, []);

    const handleAddCategory = async (newCategory) => {
        try {
            const categoryToSend = { ...newCategory };
            delete categoryToSend.questionCount;

            const res = await addDigitizationCategory(categoryToSend);
            newCategory.id = res.data.id;
            setCategories((prev) => [...prev, newCategory]);
        } catch (error) {
            console.error("Failed to add category:", error);
            setNotification({
                show: true,
                snackbarMessage: 'An error occurred. Please try again.',
                severity: 'error',
            });
        }
    };

    const handleEditCategory = async (updatedCategory) => {
        try {
            const categoryToSend = { ...updatedCategory };
            delete categoryToSend.questionCount;

            await updateDigitizationCategory(categoryToSend);
            setCategories((prev) =>
                prev.map((cat) => (cat.id === updatedCategory.id ? updatedCategory : cat))
            );
        } catch (error) {
            console.error("Failed to update category:", error);
            setNotification({
                show: true,
                snackbarMessage: 'An error occurred. Please try again.',
                severity: 'error',
            });
        }
    };

    const handleDeleteCategory = async (categoryId) => {
        try {
            await deleteDigitizationCategory(categoryId);
            setCategories((prev) => prev.filter((cat) => cat.id !== categoryId));
        } catch (error) {
            console.error("Failed to delete category:", error);
            setNotification({
                show: true,
                snackbarMessage: 'An error occurred. Please try again.',
                severity: 'error',
            });
        }
    };


    if (selectedCategory) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <SoftBox p={3}>
                    <QuestionManager
                        category={selectedCategory}
                        language={language}
                        onBack={() => setSelectedCategory(null)}
                        fetchList={fetchDigitizationCategories}
                        setNotification={setNotification}
                    />
                </SoftBox>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <SoftBox p={3}>
                {/* Stats Cards */}
                <Grid container spacing={3} mb={4}>
                    <Grid item xs={12} md={4}>
                        <Card>
                            <SoftBox p={3} display="flex" alignItems="center" gap={2}>
                                <SoftBox
                                    height="40px"
                                    width="40px"
                                    borderRadius="md"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    bgColor="info"
                                    variant="gradient"
                                >
                                    <BarChart3 size={20} color="#fff" />
                                </SoftBox>
                                <SoftBox>
                                    <SoftTypography variant="h5" fontWeight="bold">
                                        {categories.length}
                                    </SoftTypography>
                                    <SoftTypography variant="caption" color="text">
                                        Total Categories
                                    </SoftTypography>
                                </SoftBox>
                            </SoftBox>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card>
                            <SoftBox p={3} display="flex" alignItems="center" gap={2}>
                                <SoftBox
                                    height="40px"
                                    width="40px"
                                    borderRadius="md"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    bgColor="success"
                                    variant="gradient"
                                >
                                    <MessageSquare size={20} color="#fff" />
                                </SoftBox>
                                <SoftBox>
                                    <SoftTypography variant="h5" fontWeight="bold">
                                        {categories.reduce((total, cat) => total + cat.questionCount, 0)}
                                    </SoftTypography>
                                    <SoftTypography variant="caption" color="text">
                                        Total Questions
                                    </SoftTypography>
                                </SoftBox>
                            </SoftBox>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card>
                            <SoftBox p={3} display="flex" alignItems="center" gap={2}>
                                <SoftBox
                                    height="40px"
                                    width="40px"
                                    borderRadius="md"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    bgColor="warning"
                                    variant="gradient"
                                >
                                    <Globe size={20} color="#fff" />
                                </SoftBox>
                                <SoftBox>
                                    <SoftTypography variant="h5" fontWeight="bold">
                                        2
                                    </SoftTypography>
                                    <SoftTypography variant="caption" color="text">
                                        Supported Languages
                                    </SoftTypography>
                                </SoftBox>
                            </SoftBox>
                        </Card>
                    </Grid>
                </Grid>

                {/* Categories Section */}
                <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <SoftTypography variant="h5" fontWeight="medium">
                        Categories
                    </SoftTypography>
                    {categories.length > 0 && (
                        <SoftButton
                            variant="contained"
                            color="primary"
                            onClick={() => setIsAddModalOpen(true)}
                            startIcon={<Plus size={16} />}
                        >
                            Add Category
                        </SoftButton>
                    )}
                </SoftBox>

                {categories.length > 0 ? (
                    <Grid container spacing={3}>
                        {categories.map((category) => (
                            <Grid item xs={12} md={4} key={category.id}>
                                <CategoryCard
                                    category={category}
                                    language={language}
                                    onEdit={handleEditCategory}
                                    onDelete={handleDeleteCategory}
                                    onViewQuestions={(cat) => setSelectedCategory(cat)}
                                />
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Card>
                        <SoftBox p={6} textAlign="center">
                            <Brain size={64} color="grey" style={{ marginBottom: 16 }} />
                            <SoftTypography variant="h6" color="text" gutterBottom>
                                No Categories Yet
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text" mb={2}>
                                Create your first category to get started with your AI assistant.
                            </SoftTypography>
                            <SoftButton
                                variant="gradient"
                                color="primary"
                                onClick={() => setIsAddModalOpen(true)}
                            >
                                <Plus size={16} style={{ marginRight: 8 }} />
                                Create First Category
                            </SoftButton>
                        </SoftBox>
                    </Card>
                )}

                {/* Add Category Modal */}
                <EditCategoryModal
                    isOpen={isAddModalOpen}
                    onClose={() => setIsAddModalOpen(false)}
                    onSave={handleAddCategory}
                />
            </SoftBox>

            <SnackbarNotification
                notification={notification}
                setNotification={setNotification}
            />
        </DashboardLayout>
    );
};

export default ManageAiAssistant;
